import type { Account } from '@shared/products-types';
import { AccountSubtype, ProductType } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';

import type { ProductTypeOrSubtype, ProductsInCategory } from './types';
import { ProductCategory } from './types';

type ProductCategorisation = { [key in ProductCategory]?: ProductTypeOrSubtype[] };

const PRODUCT_CATEGORISATION: ProductCategorisation = {
    [ProductCategory.CURRENT]: [
        ProductType.CURRENT,
        ProductType['EXT-BANK-CURRENT'],
        AccountSubtype.INVESTMENT_CURRENT_ACCOUNT,
        AccountSubtype.INVESTMENT_FOREIGN_ACCOUNT,
    ],
    [ProductCategory.CARDS]: [
        ProductType.BANK_CARD,
        ProductType.CREDIT,
        ProductType.INDOOR,
        ProductType.PREPAID,
        ProductType.VIRTUAL_CREDIT,
        ProductType.VIRTUAL_DEBIT,
    ],
    [ProductCategory.SAVINGS]: [ProductType.SAVING],
    [ProductCategory.BUILDING_SOCIETY]: [ProductType.BUILDINGSOCIETY],
    [ProductCategory.LOANS]: [ProductType.LOAN],
    [ProductCategory.SECURITIES]: [ProductType.SECURITY],
    [ProductCategory.INSURANCE]: [ProductType.INSURANCE],
    [ProductCategory.PENSION]: [ProductType.PENSION],
    [ProductCategory.OTHER]: [
        ProductType.EXTBANK,
        AccountSubtype.AT_SECURITIES_CORPORATE_SETTLEMENT,
        AccountSubtype.AT_SECURITIES_FOND_SETTLEMENT,
        AccountSubtype.AT_SECURITIES_RETAIL_SETTLEMENT,
    ],
};

const CATEGORY_ORDINALS: { [key in ProductCategory]: number } = {
    [ProductCategory.TOP_PRODUCTS]: 100,
    [ProductCategory.CURRENT]: 200,
    [ProductCategory.CARDS]: 300,
    [ProductCategory.SAVINGS]: 400,
    [ProductCategory.BUILDING_SOCIETY]: 500,
    [ProductCategory.LOANS]: 600,
    [ProductCategory.SECURITIES]: 700,
    [ProductCategory.INSURANCE]: 800,
    [ProductCategory.PENSION]: 900,
    [ProductCategory.OTHER]: 1000,
};

function isNormalAccount(product: ProductWithHelpers): product is ProductWithHelpers<Account> {
    return 'type' in product && !!product.type;
}

function getProductSubType(product: ProductWithHelpers): AccountSubtype | undefined {
    if (isNormalAccount(product)) {
        return product.subtype;
    }

    return undefined;
}

function mapProductToCategory(product: ProductWithHelpers): ProductCategory {
    const typeCategory = Object.entries(PRODUCT_CATEGORISATION).find(([__key, categoryTypeCodes]) =>
        categoryTypeCodes.includes(product.derivedType as AccountSubtype),
    )?.[0] as ProductCategory;

    const productSubType = getProductSubType(product);
    const subTypeCategory = Object.entries(PRODUCT_CATEGORISATION).find(
        ([__key, categoryTypeCodes]) =>
            categoryTypeCodes.includes(productSubType as AccountSubtype),
    )?.[0] as ProductCategory;

    return subTypeCategory ?? typeCategory ?? ProductCategory.OTHER;
}

function categoryComparator(a: ProductsInCategory, b: ProductsInCategory): number {
    return CATEGORY_ORDINALS[a.category] - CATEGORY_ORDINALS[b.category];
}

export function groupProductsByCategory(products: ProductWithHelpers[]): ProductsInCategory[] {
    const categories = products.reduce(
        (acc, p) => {
            const category = mapProductToCategory(p);
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(p);

            return acc;
        },
        {} as Record<ProductCategory, ProductWithHelpers[]>,
    );

    const categorizedProducts = Object.entries(categories).map(
        ([category, categoryProducts]) =>
            ({
                category,
                products: categoryProducts,
            }) as ProductsInCategory,
    );

    return categorizedProducts.sort(categoryComparator);
}
